import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown, FaSearch, FaQuestionCircle } from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const BASE_URL = "https://api.dreamtoday.in";

/* ── SKELETON ── */
const SkeletonItem = () => (
    <div className="border border-yellow-100 rounded-2xl overflow-hidden animate-pulse">
        <div className="px-6 py-4 bg-white/80 flex justify-between items-center">
            <div className="h-4 bg-yellow-100 rounded w-2/3" />
            <div className="w-4 h-4 rounded bg-yellow-100" />
        </div>
    </div>
);

/* ── FAQ ITEM ── */
const FaqItem = ({ item, isOpen, onToggle }) => (
    <div className={`border rounded-2xl overflow-hidden transition-all duration-300 ${isOpen ? "border-yellow-400 shadow-md" : "border-yellow-100"
        }`}>
        <button
            onClick={onToggle}
            className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left
        bg-white/80 hover:bg-yellow-50/80 transition"
        >
            <span className={`font-semibold text-sm sm:text-base ${isOpen ? "text-yellow-700" : "text-gray-800"}`}>
                {item.question}
            </span>
            <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.25 }} className="shrink-0">
                <FaChevronDown className={`text-sm ${isOpen ? "text-yellow-500" : "text-gray-400"}`} />
            </motion.div>
        </button>

        <AnimatePresence initial={false}>
            {isOpen && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                >
                    <div className="px-6 pb-5 pt-1 bg-gradient-to-b from-yellow-50/60 to-white/80 border-t border-yellow-100">
                        <p className="text-gray-600 text-sm leading-relaxed">{item.answer}</p>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    </div>
);

const Faqs = () => {
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [openId, setOpenId] = useState(null);

    useEffect(() => {
        fetch(`${BASE_URL}/api/admin/allfaqs`)
            .then((r) => r.json())
            .then((d) => {
                if (d.success && d.faqs?.length > 0) {
                    setFaqs(d.faqs[0].faqs); // flatten from first FAQ document
                }
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        if (!search.trim()) return faqs;
        return faqs.filter(
            (item) =>
                item.question.toLowerCase().includes(search.toLowerCase()) ||
                item.answer.toLowerCase().includes(search.toLowerCase())
        );
    }, [search, faqs]);

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-gradient-to-b from-white via-[#FFF7DB] to-white pt-28 pb-20 px-4 relative overflow-hidden">

                {/* BG DECORATIONS */}
                <div className="absolute top-20 right-0 w-72 h-72 rounded-full
          bg-gradient-to-br from-yellow-100 to-amber-50 opacity-50 blur-3xl translate-x-1/2 pointer-events-none" />
                <div className="absolute bottom-10 left-0 w-60 h-60 rounded-full
          bg-gradient-to-br from-amber-100 to-yellow-50 opacity-40 blur-3xl -translate-x-1/3 pointer-events-none" />

                <div className="max-w-4xl mx-auto relative z-10">

                    {/* HERO */}
                    <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-14">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500
              flex items-center justify-center shadow-lg mx-auto mb-5">
                            <FaQuestionCircle className="text-white text-2xl" />
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold mb-4
              bg-gradient-to-r from-yellow-600 via-yellow-700 to-yellow-800
              bg-clip-text text-transparent">
                            Frequently Asked Questions
                        </h1>
                        <p className="text-gray-500 max-w-xl mx-auto">
                            Everything you need to know about PrizePulse. Can't find your answer?{" "}
                            <a href="/contactus" className="text-yellow-600 font-medium hover:underline">Contact us</a>
                        </p>
                    </motion.div>

                    {/* SEARCH */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-10">
                        <div className="flex items-center bg-white border border-yellow-300 rounded-2xl px-5 py-4 shadow-sm">
                            <FaSearch className="text-yellow-600 mr-3 shrink-0" />
                            <input
                                type="text"
                                placeholder="Search questions..."
                                className="w-full outline-none text-gray-700 bg-transparent"
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setOpenId(null); }}
                            />
                            {search && (
                                <button onClick={() => setSearch("")} className="ml-2 text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
                            )}
                        </div>
                        {search && !loading && (
                            <p className="text-xs text-gray-400 mt-2 pl-1">
                                {filtered.length} result{filtered.length !== 1 ? "s" : ""} found
                            </p>
                        )}
                    </motion.div>

                    {/* LOADING */}
                    {loading && (
                        <div className="space-y-3">
                            {Array(5).fill(null).map((_, i) => <SkeletonItem key={i} />)}
                        </div>
                    )}

                    {/* FAQ LIST — flat, no categories (API has no category field) */}
                    {!loading && filtered.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.05 }}
                            className="space-y-3"
                        >
                            {filtered.map((item, i) => (
                                <FaqItem
                                    key={item._id}
                                    item={item}
                                    isOpen={openId === item._id}
                                    onToggle={() => setOpenId(openId === item._id ? null : item._id)}
                                />
                            ))}
                        </motion.div>
                    )}

                    {/* EMPTY — no results from search */}
                    {!loading && faqs.length > 0 && filtered.length === 0 && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                            className="flex flex-col items-center py-20 text-center">
                            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-100 to-amber-200
                border border-yellow-300 flex items-center justify-center shadow mb-5">
                                <FaQuestionCircle className="text-yellow-400 text-4xl" />
                            </div>
                            <h3 className="text-xl font-bold text-yellow-800 mb-2">No results found</h3>
                            <p className="text-gray-400 text-sm max-w-xs">
                                Try different keywords or{" "}
                                <a href="/contactus" className="text-yellow-600 hover:underline">contact our support team</a>.
                            </p>
                        </motion.div>
                    )}

                    {/* EMPTY — no FAQs from API at all */}
                    {!loading && faqs.length === 0 && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                            className="flex flex-col items-center py-20 text-center">
                            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-100 to-amber-200
                border border-yellow-300 flex items-center justify-center shadow mb-5">
                                <FaQuestionCircle className="text-yellow-400 text-4xl" />
                            </div>
                            <h3 className="text-xl font-bold text-yellow-800 mb-2">No FAQs Available</h3>
                            <p className="text-gray-400 text-sm max-w-xs">
                                Check back soon or{" "}
                                <a href="/contactus" className="text-yellow-600 hover:underline">contact our support team</a> directly.
                            </p>
                        </motion.div>
                    )}

                    {/* BOTTOM CTA */}
                    {!loading && (
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="mt-16 text-center bg-gradient-to-r from-yellow-50 to-amber-50
                border border-yellow-200 rounded-3xl p-8 shadow-sm"
                        >
                            <h3 className="text-xl font-bold text-gray-800 mb-2">Still have questions?</h3>
                            <p className="text-gray-500 text-sm mb-5">
                                Our support team is available Monday to Saturday, 9AM–6PM.
                            </p>
                            <a
                                href="/contactus"
                                className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold text-white
                  bg-gradient-to-r from-yellow-500 to-amber-600 shadow-md hover:scale-105 transition"
                            >
                                Contact Support
                            </a>
                        </motion.div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
};

export default Faqs;